/**
 * Utility functions for date interval selection in the availability calendar
 */

const MONTH_NAMES_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

/**
 * True iff `value` is a real calendar date in strict `YYYY-MM-DD` form.
 *
 * The shape regex alone is not enough: `2027-13-45` (month 13, day 45) passes
 * a `/^\d{4}-\d{2}-\d{2}$/` test but is not a date. We additionally parse it
 * (UTC, to avoid timezone drift) and round-trip it back to a string — an
 * impossible date either fails to parse (`NaN`) or normalises to a different
 * string, so it's rejected. Pure; the checkout API routes gate on this before
 * touching PYN (docs/82 · A7), so garbage like `2027-13-45` can't reach the
 * upstream booking engine.
 */
export function isValidIsoDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00Z`);
  return (
    !Number.isNaN(parsed.getTime()) &&
    parsed.toISOString().slice(0, 10) === value
  );
}

/**
 * Reduce any date-ish string to its `YYYY-MM-DD` date part. Accepts plain
 * dates (`2027-01-01`, returned unchanged) and datetimes
 * (`2027-01-01T00:00:00Z` → `2027-01-01`). Used to compare dates from external
 * systems (PYN PMS) defensively: if PYN ever serialises a datetime instead of a
 * plain date, a raw `===` would silently treat every booking as "dates
 * changed" and disable the double-book overlay guard (docs/82 · A7). Normalise
 * both sides first so the comparison survives a serialisation change.
 */
export function normalizeToIsoDate(value: string): string {
  return value.length >= 10 ? value.slice(0, 10) : value;
}

/**
 * Compare two ISO date strings (YYYY-MM-DD format)
 * @returns negative if a < b, 0 if equal, positive if a > b
 */
export function compareDates(a: string, b: string): number {
  return a.localeCompare(b);
}

/**
 * Check if a date is in range (checkIn inclusive, checkOut exclusive)
 * This matches hotel booking logic where checkout day is not a "night"
 */
export function isDateInRange(date: string, checkIn: string, checkOut: string): boolean {
  return compareDates(date, checkIn) >= 0 && compareDates(date, checkOut) < 0;
}

/**
 * Check if a date is strictly between checkIn and checkOut (both exclusive)
 * Used for highlighting the "middle" days of a selection
 */
export function isDateBetween(date: string, checkIn: string, checkOut: string): boolean {
  return compareDates(date, checkIn) > 0 && compareDates(date, checkOut) < 0;
}

/**
 * Format date as YYYY-MM-DD in local timezone (not UTC)
 */
function formatDateLocal(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Add one day to an ISO date string
 */
export function addOneDay(dateStr: string): string {
  const date = new Date(dateStr + 'T12:00:00'); // Use noon to avoid DST issues
  date.setDate(date.getDate() + 1);
  return formatDateLocal(date);
}

/**
 * Add `n` days to an ISO date string (n ≥ 1). Used by the min-stay first-click
 * auto-bump (doc 81 §12): clicking a check-in with `minStay = N` sets check-out
 * to `addDays(checkIn, N)`. Noon anchor avoids DST edge shifts.
 */
export function addDays(dateStr: string, n: number): string {
  const date = new Date(dateStr + 'T12:00:00');
  date.setDate(date.getDate() + n);
  return formatDateLocal(date);
}

/**
 * Get all dates between start and end (start inclusive, end exclusive)
 */
export function getDatesBetween(start: string, end: string): string[] {
  const dates: string[] = [];
  let current = start;
  while (compareDates(current, end) < 0) {
    dates.push(current);
    current = addOneDay(current);
  }
  return dates;
}

/**
 * Calculate number of nights between check-in and check-out
 */
export function calculateNights(checkIn: string, checkOut: string): number {
  const startDate = new Date(checkIn + 'T12:00:00');
  const endDate = new Date(checkOut + 'T12:00:00');
  const diffTime = endDate.getTime() - startDate.getTime();
  return Math.round(diffTime / (1000 * 60 * 60 * 24));
}

export interface IntervalPrice {
  nights: number;
  /** Per-night prices that were actually found in `calendarDays`. */
  nightlyPrices: number[];
  /** Sum of `nightlyPrices` — only a TRUE total when `hasAllPrices`. */
  totalPrice: number;
  /** True iff every night in the range had a known price. */
  hasAllPrices: boolean;
}

/**
 * Sum the nightly prices for a stay and report whether EVERY night had a
 * known price. Pure; shared by the room card and the room-detail booking
 * summary (docs/82 · C6/C7). A stay that crosses into a month the client
 * hasn't loaded yet yields `hasAllPrices === false` with a PARTIAL
 * `totalPrice` — callers MUST gate any displayed total on `hasAllPrices` and
 * withhold the figure when it is false, otherwise the guest sees a too-low
 * total next to "Book Now".
 */
export function computeIntervalPrice(
  checkIn: string,
  checkOut: string,
  calendarDays: { date: string; price: number | null }[],
): IntervalPrice {
  const nights = calculateNights(checkIn, checkOut);
  const datesInRange = getDatesBetween(checkIn, checkOut);
  const nightlyPrices: number[] = [];
  for (const date of datesInRange) {
    const dayData = calendarDays.find((d) => d.date === date);
    if (dayData?.price !== null && dayData?.price !== undefined) {
      nightlyPrices.push(dayData.price);
    }
  }
  const totalPrice = nightlyPrices.reduce((sum, p) => sum + p, 0);
  return {
    nights,
    nightlyPrices,
    totalPrice,
    hasAllPrices: nightlyPrices.length === nights,
  };
}

/**
 * Format date as "Dec 15" style
 */
export function formatShortDate(dateStr: string): string {
  const [, month, day] = dateStr.split('-').map(Number);
  return `${MONTH_NAMES_SHORT[month - 1]} ${day}`;
}

/**
 * Get month key from date string for caching (e.g., "2025-12")
 */
export function getMonthKey(dateStr: string): string {
  return dateStr.substring(0, 7); // "YYYY-MM"
}

/**
 * Get month key from year and month numbers
 */
export function getMonthKeyFromParts(year: number, month: number): string {
  return `${year}-${String(month).padStart(2, '0')}`;
}
